/* @flow */
import * as React from 'react';
import gql from 'graphql-tag';
import { graphql, compose } from 'react-apollo';
import idx from 'idx';
import { Container } from 'flux/utils';
import { once } from 'lodash';

import Group from 'entities/Group';
import NavigationTabs from 'components/NavigationTabs.jsx';
import UnreadStore from 'stores/UnreadStore';

export function showPeopleTab(group: Group): boolean {
  return group.getType() !== 'district' && !group.isRoot();
}

function showFilesTab(group: Group, currentUser): boolean {
  if (!group || !currentUser || group.isStaffClass()) {
    return false;
  }

  return currentUser.has_files;
}

function shouldShowMembershipCount(group: Group, currentUser) {
  return (
    !group.isRoot() &&
    group.getMembershipsCount() > 2 &&
    (group.isOwnedByUser(currentUser.id) || group.isSearchable())
  );
}

type Props = {
  adminTheme: ?boolean,
  group: Group,
  data: ReactApolloData<GroupNavigationTabsQuery>,
  orgsData: ReactApolloData<GroupNavigationTabsOrganizationQuery>,
};

type State = {
  unreadCount: number,
};

class GroupNavigationTabs extends React.Component<Props, State> {
  state = {
    unreadCount: 0,
  };

  static getStores() {
    return [UnreadStore];
  }

  static calculateState(state: State, props: Props) {
    const { group } = props;
    const groupUuid = group.getUuid();

    if (!groupUuid) {
      return {};
    }

    const isDistrict = group.getType() === 'district';
    const org = group.getOrganization();
    const orgUuid = org ? org.get('uuid') : null;
    const unreadCount =
      isDistrict && orgUuid
        ? UnreadStore.getUnreadCount(groupUuid) + UnreadStore.getUnreadCount(orgUuid)
        : UnreadStore.getUnreadCount(groupUuid);
    return {
      // $FlowFixMe possible null
      unreadCount,
    };
  }

  _logError = once(() => {
    console.error(
      "Membership count and org membership counts don't sync. Staff Class is out of sync"
    );
  });

  hasUnreads = () => {
    return this.state.unreadCount > 0;
  };

  render() {
    const {
      adminTheme,
      group,
      data: { user },
      orgsData,
      ...others
    } = this.props;

    const showMembershipCount = user ? shouldShowMembershipCount(group, user) : false;
    const membershipCount = group.getMembershipsCount();
    const orgMembershipCount = idx(orgsData, _ => _.organizations[0].membersCount);

    const phoneCallsFeature = user ? user.has_phone_calls : false;
    const canPhoneCall = phoneCallsFeature || !!idx(user, _ => _.permissions.canMakePhoneCalls);

    const permissions = idx(orgsData, _ => _.organizations[0].permissions.adminConsole);
    const adminPermissions = permissions || {};

    const baseUrl = `/classes/${group.getCodeOrError()}`;
    const tabs = [];

    if (
      showMembershipCount &&
      orgMembershipCount &&
      Math.abs(membershipCount - orgMembershipCount) < 20
    ) {
      this._logError();
    }

    if (adminPermissions.seeHomeTab) {
      tabs.push({
        name: 'Overview',
        path: `${baseUrl}/home`,
      });
    }

    if (group.get('hasMessagesTab')) {
      tabs.push({
        name: 'Messages',
        path: `${baseUrl}/messages`,
        indicator: this.hasUnreads(),
      });
    }

    if (showFilesTab(group, user)) {
      tabs.push({
        name: 'Files',
        path: `${baseUrl}/files`,
      });
    }

    if (showPeopleTab(group)) {
      tabs.push({
        name: showMembershipCount ? `People (${membershipCount})` : 'People',
        path: `${baseUrl}/people`,
      });
    }

    if (canPhoneCall) {
      tabs.push({
        name: 'Calls',
        path: `${baseUrl}/calls`,
      });
    }

    if (adminPermissions.seeClassesTab) {
      tabs.push({
        name: 'Classes',
        path: `${baseUrl}/classes`,
      });
    }

    if (adminPermissions.seeSchoolsTab) {
      tabs.push({
        name: 'Schools',
        path: `${baseUrl}/schools`,
      });
    }

    if (adminPermissions.seeStatisticsTab) {
      tabs.push({
        name: 'Statistics',
        path: `${baseUrl}/statistics`,
      });
    }

    if (adminPermissions.seeSettingsTab) {
      tabs.push({
        name: 'Settings',
        path: `${baseUrl}/settings/organization`,
      });
    }

    if (orgsData && orgsData.loading) {
      return null;
    }

    return (
      <NavigationTabs
        {...others}
        tabs={tabs}
        theme={adminTheme ? 'light' : 'dark'}
        className="GroupNavigationTabs"
      />
    );
  }
}

const withFeature = graphql(
  gql`
    query GroupNavigationTabs {
      user: me {
        uuid
        id
        has_phone_calls: has_feature(name: "phone_calls")
        has_files: has_feature(name: "files_tab.web")
        permissions {
          canMakePhoneCalls
        }
      }
    }
  `
);

const withOrgPermissions = graphql(
  gql`
    query GroupNavigationTabsOrganization($organizationId: Int) {
      organizations(ids: [$organizationId]) {
        id
        uuid
        type
        membersCount
        permissions {
          adminConsole {
            seeHomeTab
            seeSchoolsTab
            seePeopleTab
            seeClassesTab
            seeStatisticsTab
            seeSettingsTab
          }
        }
      }
    }
  `,
  {
    options: ({ group }) => ({
      variables: { organizationId: group.getStaffClassOrgId() },
    }),
    skip: ({ group }) => !group.isStaffClass(),
    props: ({ data }) => ({ orgsData: data }),
  }
);

export { GroupNavigationTabs };
export default compose(
  withFeature,
  withOrgPermissions
)(
  Container.create(GroupNavigationTabs, {
    withProps: true,
    pure: false,
  })
);
